import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  BulkSaveMyScriptItemsMutation,
  BulkSaveSummaryItemsMutation,
  CompleteMyInterviewMutation,
  CompleteSummaryCheckMutation,
  EnsureInterviewScriptMutation,
  InterviewScriptQuery,
  MyInterviewQuery,
  MyInterviewsQuery,
  SelfInterviewSummaryItemsQuery,
  UpdateMyInterviewStatusMutation,
  UpdateMyScriptItemMutation,
  UpdateSummaryItemMutation,
  UseCasesQuery,
} from "../graphql/myInterviews.graphql";

export interface MyInterview {
  interviewId: string;
  candidateId: string;
  candidateName: string | null;
  candidateNameKana: string | null;
  candidateEmail: string | null;
  termId: string | null;
  termTitle: string | null;
  termNotionUrl: string | null;
  sponsorId: string | null;
  sponsorName: string | null;
  startTime: string;
  endTime: string;
  zoomMeetingUrl: string | null;
  zoomRecordingViewUrl: string | null;
  zoomMeetingStartUrl: string | null;
  /** ステークホルダーインタビューだけが持つ情報。従業員インタビューでは null */
  stakeholderContext: {
    stakeholderSettingName: string;
    recipientNames: readonly string[];
    /** 回答は要約シートからの取り込みが正で、ここが揃った時点がこのインタビューの終わり */
    answersImported: boolean;
  } | null;
  status: string;
  rawVttText: string | null;
  completedAt: string | null;
  summaryCheckCompletedAt: string | null;
  satisfactionScore: number | null;
  behaviorChangeScore: number | null;
  handoverNote: string | null;
  coachReportNg: boolean | null;
  interviewerInsight: string | null;
  internalUseAllowed: boolean | null;
  noConfidentialInfo: boolean;
  confidentialInfo: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface MyScriptItem {
  scriptItemId: string;
  scriptId: string;
  category: string;
  timeEstimate: string | null;
  itemName: string;
  isRequired: boolean;
  question: string;
  answer: string | null;
  memo: string | null;
  note: string | null;
  order: number;
  orderIndex: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UpdateMyScriptItemInput {
  scriptItemId: string;
  answerMemo?: string | null;
  interviewerMemo?: string | null;
  question?: string | null;
  note?: string | null;
}

export interface CompleteInterviewInput {
  interviewId: string;
  handoverNote?: string;
  coachReportNg: boolean;
  interviewerInsight?: string;
  internalUseAllowed: boolean;
  hasConfidentialInfo: boolean;
  confidentialInfo?: string;
}

export interface UseCase {
  useCaseId: string;
  category: string;
  cognitiveAction: string;
  name: string;
}

export interface SummaryItem {
  summaryItemId: string;
  interviewId: string;
  category: string;
  content: string;
  useCaseId: string | null;
  order: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UpdateSummaryItemInput {
  summaryItemId: string;
  content?: string | null;
  useCaseId?: string | null;
}

export interface BulkSaveSummaryItemInput {
  summaryItemId?: string | null;
  category: string;
  content: string;
  useCaseId?: string | null;
  order: number;
}

export interface BulkSaveSummaryItemsInput {
  interviewId: string;
  items: BulkSaveSummaryItemInput[];
  satisfactionScore?: number | null;
  behaviorChangeScore?: number | null;
}

export interface BulkSaveScriptItemInput {
  scriptItemId?: string | null;
  category: string;
  timeEstimate: string;
  itemName: string;
  question?: string | null;
  note?: string | null;
  isRequired: boolean;
  order: number;
}

export interface BulkSaveScriptItemsInput {
  interviewId: string;
  items: BulkSaveScriptItemInput[];
}

export function useMyInterviewsGraphQL() {
  const [result, reexecute] = useQuery({ query: MyInterviewsQuery });

  return {
    interviews: result.data?.myInterviews.nodes ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useMyInterviewGraphQL(interviewId: string | null) {
  const [result, reexecute] = useQuery({
    query: MyInterviewQuery,
    variables: { interviewId: interviewId ?? "" },
    pause: !interviewId,
  });

  return {
    interview: result.data?.myInterview ?? null,
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useInterviewScriptGraphQL(interviewId: string | null) {
  const [result, reexecute] = useQuery({
    query: InterviewScriptQuery,
    variables: { interviewId: interviewId ?? "" },
    pause: !interviewId,
  });

  return {
    scriptItems: result.data?.interviewScript.nodes ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useEnsureInterviewScript() {
  const [, executeEnsure] = useMutation(EnsureInterviewScriptMutation);
  return useCallback(
    async (interviewId: string) => {
      const result = await executeEnsure({ interviewId });
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data?.ensureInterviewScript.nodes ?? [];
    },
    [executeEnsure],
  );
}

export function useMyInterviewMutations() {
  const [, executeUpdateScriptItem] = useMutation(UpdateMyScriptItemMutation);
  const [, executeCompleteInterview] = useMutation(CompleteMyInterviewMutation);
  const [, executeUpdateStatus] = useMutation(UpdateMyInterviewStatusMutation);
  const [, executeBulkSaveScriptItems] = useMutation(
    BulkSaveMyScriptItemsMutation,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateScriptItem = useCallback(
    async (input: UpdateMyScriptItemInput): Promise<MyScriptItem | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeUpdateScriptItem({ input });
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result.data?.updateMyScriptItem ?? null;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "スクリプト項目の更新に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdateScriptItem],
  );

  const completeInterview = useCallback(
    async (
      input: CompleteInterviewInput,
    ): Promise<{ success: boolean; errorMessage: string | null } | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeCompleteInterview({ input });
        if (result.error) {
          throw new Error(result.error.message);
        }
        if (!result.data?.completeMyInterview) {
          return null;
        }
        return {
          success: result.data.completeMyInterview.success,
          errorMessage: result.data.completeMyInterview.errorMessage ?? null,
        };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "インタビューの完了に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeCompleteInterview],
  );

  const updateMyInterviewStatus = useCallback(
    async (interviewId: string, status: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeUpdateStatus({ interviewId, status });
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result.data?.updateMyInterviewStatus ?? null;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "インタビューステータスの更新に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdateStatus],
  );

  const bulkSaveScriptItems = useCallback(
    async (
      input: BulkSaveScriptItemsInput,
    ): Promise<{
      success: boolean;
      items: MyScriptItem[];
      errorMessage: string | null;
    } | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeBulkSaveScriptItems({ input });
        if (result.error) {
          throw new Error(result.error.message);
        }
        if (!result.data?.bulkSaveMyScriptItems) {
          return null;
        }
        return {
          success: result.data.bulkSaveMyScriptItems.success,
          items: result.data.bulkSaveMyScriptItems.items,
          errorMessage: result.data.bulkSaveMyScriptItems.errorMessage ?? null,
        };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "スクリプトの一括保存に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeBulkSaveScriptItems],
  );

  return {
    updateScriptItem,
    bulkSaveScriptItems,
    completeInterview,
    updateMyInterviewStatus,
    loading,
    error,
  };
}

export function useUseCasesGraphQL() {
  const [result, reexecute] = useQuery({ query: UseCasesQuery });

  return {
    useCases: result.data?.useCases.nodes ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useInterviewSummaryItemsGraphQL(interviewId: string | null) {
  const [result, reexecute] = useQuery({
    query: SelfInterviewSummaryItemsQuery,
    variables: { interviewId: interviewId ?? "" },
    pause: !interviewId,
  });

  return {
    summaryItems: result.data?.interviewSummaryItems.nodes ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useSummaryCheckMutations() {
  const [, executeUpdateSummaryItem] = useMutation(UpdateSummaryItemMutation);
  const [, executeBulkSaveSummaryItems] = useMutation(
    BulkSaveSummaryItemsMutation,
  );
  const [, executeCompleteSummaryCheck] = useMutation(
    CompleteSummaryCheckMutation,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateSummaryItem = useCallback(
    async (input: UpdateSummaryItemInput): Promise<SummaryItem | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeUpdateSummaryItem({ input });
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result.data?.updateSummaryItem ?? null;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "要約アイテムの更新に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdateSummaryItem],
  );

  const bulkSaveSummaryItems = useCallback(
    async (
      input: BulkSaveSummaryItemsInput,
    ): Promise<{
      success: boolean;
      items: SummaryItem[];
      errorMessage: string | null;
    } | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeBulkSaveSummaryItems({ input });
        if (result.error) {
          throw new Error(result.error.message);
        }
        if (!result.data?.bulkSaveSummaryItems) {
          return null;
        }
        return {
          success: result.data.bulkSaveSummaryItems.success,
          items: result.data.bulkSaveSummaryItems.items,
          errorMessage: result.data.bulkSaveSummaryItems.errorMessage ?? null,
        };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "要約アイテムの一括保存に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeBulkSaveSummaryItems],
  );

  const completeSummaryCheck = useCallback(
    async (
      interviewId: string,
      satisfactionScore?: number | null,
      behaviorChangeScore?: number | null,
    ): Promise<{
      success: boolean;
      status: string | null;
      summaryCheckCompletedAt: string | null;
      errorMessage: string | null;
    } | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeCompleteSummaryCheck({
          input: {
            interviewId,
            satisfactionScore: satisfactionScore ?? null,
            behaviorChangeScore: behaviorChangeScore ?? null,
          },
        });
        if (result.error) {
          throw new Error(result.error.message);
        }
        if (!result.data?.completeSummaryCheck) {
          return null;
        }
        return {
          success: result.data.completeSummaryCheck.success,
          status: result.data.completeSummaryCheck.status ?? null,
          summaryCheckCompletedAt:
            result.data.completeSummaryCheck.summaryCheckCompletedAt ?? null,
          errorMessage: result.data.completeSummaryCheck.errorMessage ?? null,
        };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "要約チェックの完了に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeCompleteSummaryCheck],
  );

  return {
    updateSummaryItem,
    bulkSaveSummaryItems,
    completeSummaryCheck,
    loading,
    error,
  };
}
